/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp. 2016, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 * @module dashboard/filters/pagecontext/PageContext
 * @see PageContext
 */

define([
	'../../lib/@waca/dashboard-common/dist/core/Collection',
	'./PageContextEntry',
	'./PageContextResult',
	'underscore',
	'../../lib/@waca/core-client/js/core-client/utils/UniqueId',
	'../FilterMetadataHelper'
], function(Collection, PageContextEntry, PageContextResult, _, UniqueID, FilterMetadataHelper) {

	/**
	 * The PageContext (also called runtimeContext) represents the filter/selection
	 * (and possibly other) states of data on a page or tab as a collection.
	 * Its content is persisted to the dashboard spec separately from the other contents of the dashboard.
	 *
	 * It is designed to be flexible to access various subsets of whats stored by using selectors on items.
	 * (for example...all global filters or all visualization pieces for a particular tab or only context for a particular item).
	 * as well, it will be possible to obtain a 'net' state (which is the result of combining global, local and user-specific states).
	 *
	 * This context is independent of widget specs and is applied at runtime AND
	 * Long term it will provide a user-specific view of the same page...even to users who have no write access to that page.
	 * Clients (like widgets or the filter shelf) can subscribe to events that are thrown when the context changes.
	 */
	var PageContext = Collection.extend({
		modelClass: PageContextEntry,

		// constructor
		init: function(spec, options = {}) {
			//Call inherited to initialize the pageContextEntry models if there is a supplied pageContextSpec.
			PageContext.inherited('init', this, arguments);

			this.logger = options.logger;
			this.boardModel = options.boardModel;

			this.dashboardApi = options.dashboardApi;

			if (!this.dashboardApi) {
				throw new Error('dashboardApi is not provided');
			}


			//the public api for the pageContext.
			this.api = {
				updateFilterContext: this.updateFilterContext.bind(this),
				updateBrushingContext: this.updateBrushingContext.bind(this),
				isBrushed: this._isBrushed.bind(this),
				getPageContextItems: this.getPageContextItems.bind(this),
				getPageContextItem: this.getPageContextItem.bind(this),
				getNetPageContextItems: this.getNetPageContextItems.bind(this),
				getNetPageContext: this.getNetPageContext.bind(this),
				getNetPageContextSpec: this.getNetPageContextSpec.bind(this),
				deletePageContextItems: this.deletePageContextItems.bind(this),
				deleteVisTypePageContext: this.deleteVisTypePageContext.bind(this),
				clearPageContextItemValues: this.clearPageContextItemValues.bind(this),
				reOrderPageContextItems: this.reOrderPageContextItems.bind(this),
				getNumOfPageContextItems: this.getNumOfPageContextItems.bind(this),
				updateEventGroupId: this.updateEventGroupId.bind(this),
				resetToPersistedValues: this.resetToPersistedValues.bind(this)
			};
			if(this.boardModel) {
				this.boardModel.on('duplicateLayout', this.copyPageContextItemsAndRescope, this);
				this.boardModel.on('removeLayouts', this._removeLayoutsHandler, this);
			}
			this.dashboardApi.on('shapingmodel:changed', this._onShapingModelChanged, this);

			//Ensure that pageContextEntries have a way to obtain their related global context.
			this.each(function(pageContextEntry) {
				this._setGlobalContextCb(pageContextEntry);
			}.bind(this));
		},

		destroy: function() {
			this.dashboardApi.off('shapingmodel:changed', this._onShapingModelChanged, this);
			if (this.boardModel) {
				this.boardModel.off('duplicateLayout', this.copyPageContextItemsAndRescope, this);
			}
			this.scopedModels = {};
		},

		/**
		 * @returns the public api to the pageContext that is accessible to clients.
		 */
		getAPI: function() {
			return this.api;
		},


		/**
		 * Public pageContext api's allow selectors in a simpler form.  These need to be mapped for internal use.
		 * For example, externally, the api allows 'itemId' of type string rather than 'hierarchyUniqueNames' of type array.
		 * @param selectors - the selectors as specified in public api methods.
		 * @returns selectors in a form that is used internally by the page context.
		 */
		_mapSelectors: function(selectors) {
			var finalSelectors = selectors;
			if (selectors &&
				(selectors.itemName ||
					selectors.itemId ||
					selectors.itemAliasName)) {
				finalSelectors = _.clone(selectors);
				if (selectors.itemAliasName) {
					finalSelectors.filterName = selectors.itemAliasName;
					delete finalSelectors.itemAliasName;
				}
				if (selectors.itemId) {
					finalSelectors.hierarchyUniqueNames = [selectors.itemId];
					delete finalSelectors.itemId;
				}
				if (selectors.itemName) {
					finalSelectors.hierarchyNames = [selectors.itemName];
					delete finalSelectors.itemName;
				}
			}
			return finalSelectors;
		},

		_addUpdateFilterContextEntry: function(inputItemContext, update, options, itemContext) {
			options = options || {};
			options.payloadData = options.payloadData || {};
			options.payloadData.undoRedoTransactionId = options.payloadData.undoRedoTransactionId || UniqueID.get('update_filter');
			//Whenever the filter context changes, brushing must be cleared for the widgets/clear buttons that are in scope...

			//NOTE: Code to clear brushing a filter change has been removed (ie: _shouldClearBrushing and _removeBrushingContextEntriesOnFilterChange)
			//This is a PM Decision based on r6 behaviour and for consistency with shaping filter behaviour.

			var initialSize = this.size();
			var response = {
				ignorePageContextChanged: options.payloadData.ignorePageContextChanged
			};
			response.payloadData = options.payloadData;
			var existingEntry = this._findOrCreateFilterContextEntry(itemContext, update, options, response);
			if (existingEntry) {
				//Set both the item and the items member in the response to indicate its a single result but to ease client processing.
				response.items = [existingEntry.getAPI()];
				response.updated = true;
				response.added = !options.skipFilterFocus && response.added;

				var contextEntryChanged = false;
				if (itemContext.filterName && existingEntry.filterName !== itemContext.filterName) {
					existingEntry.set({
						filterName: itemContext.filterName
					});
					contextEntryChanged = true;
				}
				if (itemContext.metadataSourceId) {
					existingEntry.metadataSourceId = itemContext.metadataSourceId;
				}
				if (!_.isUndefined(update.exclude) && existingEntry.get('exclude') !== update.exclude) {
					existingEntry.setExclude(update.exclude,options);
					contextEntryChanged = true;
				}
				if (this._handleClearValues(update.command, existingEntry, response) ||
					existingEntry.mergeValues(update, options) ||
					this.size() !== initialSize ||
					contextEntryChanged || itemContext.isModeledFilter) {
					if (update.command==='remove' && existingEntry.origin==='visualization' && existingEntry.getValueCount()===0) {
						this.deletePageContextItems(itemContext, options);
					} else {
						//Pass the item context as passed in back to the caller.
						this._filterContextUpdated(inputItemContext, response);
					}
				}
				return existingEntry;
			}
		},

		_handleClearValues: function(command, existingEntry, response) {
			if (command === 'clearValues') {
				existingEntry.clearValues();
				response.cleared = true;
				return true;
			}
			return false;
		},
		/**
		 * Called by clients such as the filter widget to update the page context with a
		 * filter update.  A filter update could represent new selections (member list, value range, expression)
		 * being added/removed based on an interactive session from a filter widget.  It is expected that any update
		 * only applies to a SINGLE item.
		 * @param itemContext - an object that represents the source of the update
		 * @param update - an object that represents the value of the filter
		 * @param options - options on the request which may include beforeItem (meaning where to insert the item.
		 *				If no beforeItem is specified or if beforeItemis specified but undefined, the new entry will be added/removed from the end.
		 *					{
		 * 						command: 'update|remove\removeAll|delete',
		 *						values: [
		 *							key1, key2, key3 (as returned in the key field of getMemberTree)
		 *						]
		 *						or, for ranges:
		 *						condition: [
		 *							levelIds: []  (optional)
		 *							from: [min1,min2],
		 *							to: [max1,max2]
		 *						]
		 *					}
		 */

		updateFilterContext: function(inputItemContext, update, options) {
			options = options || {};

			var itemContext = this._mapSelectors(inputItemContext);
			if (update.command === 'delete') {
				return this.deletePageContextItems(itemContext, options); //delete notifies
			} else {
				this._checkItemContext(itemContext, /*unique*/ true);
				return this._addUpdateFilterContextEntry(inputItemContext, update, options, itemContext);
			}
		},

		/**
		 * add one or more items to the brushing context (entries of type "origin: 'visualization'" because brushing is initiated from visualizations not filters).
		 * @param brushingContextItems - an array of brushing context items which are very similar to page context items EXCEPT they can be in a more 'raw form'....meaning:
		 *								 An array entry can define a single point or edge item and this api will automatically group points as tupleSet entries under the proper hierarchies(s)
		 *								 eg  2 array entries might be  points  (2006, Camping  and 2008, Outdoor each defining Year and ProductLine).
		 *								 These multiple entries will be combined into tupleSets under a single hierarchies context item.
		 * @param options - one or more options for adding
		 *			append: (optional) In the case of a card, a selection in a single visualization sets the entire visualization for a tab (scope), howerver; if append = true,
		 *					the brushing context for the visualization will be appended to an existing one.
		 *			brushingOptions: For the current implementation of brushing, scope should always be set to the scope of the source visualization..because brushing is applied to the current tab.
		 *							  It will default to global if not set.
		 */
		updateBrushingContext: function(brushingContextItems, brushingOptions) {
			//Unless the option is set to append, clear visualizations entries at the appropriate scope prior to adding new ones.
			brushingOptions = brushingOptions || {};

			var transactionId = UniqueID.get('update_brushing_');

			var response = {
				items: []
			};
			_.each(brushingContextItems, function(brushingContextItem) {
				var existingEntry = this._getBrushingContextEntry(brushingContextItem);
				if (!existingEntry) {
					existingEntry = this._addPageContextEntry(brushingContextItem, {
						payloadData: {
							undoRedoTransactionId: transactionId
						}
					});
				} else {
					existingEntry.addToTupleSet(brushingContextItem.tupleSet, {
						payloadData: {
							undoRedoTransactionId: transactionId
						}
					});
				}
				response.items.push(existingEntry);
				response.updated = true;
			}.bind(this));

			if (response.items.length) {
				//Notify widgets if the brushing context changes - previous selections were removed and/or new selections were added
				this._brushingContextUpdated(brushingOptions, response);
			}
		},

		_getBrushingContextEntry: function(brushingContextItem) {
			var selectors = {
				origin: 'visualization',
				scope: brushingContextItem.scope
			};

			if (brushingContextItem.sourceId) {
				selectors.sourceId = brushingContextItem.sourceId;
			}
			if (brushingContextItem.hierarchies) {
				selectors.hierarchies = brushingContextItem.hierarchies;
			}

			var existingEntries = this._getPageContextEntries(selectors);
			var existingEntry = (existingEntries && existingEntries.length > 0) ? existingEntries[0] : null;
			return existingEntry;
		},

		_isBrushed: function(brushingContextItem) {
			return this._getBrushingContextEntry(brushingContextItem) ? true : false;
		},

		_updateModifiedAssets: function(sourceIdsToUpdate, modified, pageContextEntry) {
			if (modified) {
				sourceIdsToUpdate[pageContextEntry.getSourceId()] = true;
			}
		},

		/**
		 * Clear the selected values for all page context items that match the selector.
		 * This function is different from deletePageContextItems in that entries remain, they simply are unfiltered.
		 * @param selectors - the items to select
		 * @param options - Includes an options transaction id (for undo/redo to group clear events with others) and a silent flag to avoid notifying for intermediate steps.
		 * @returns true if data changed and others were notified.
		 */
		clearPageContextItemValues: function(selectors, options) {
			options = options || {};
			var payloadData = options.payloadData = options.payloadData || {};
			payloadData.undoRedoTransactionId = payloadData.undoRedoTransactionId || UniqueID.get('clear_values_');
			selectors = this._mapSelectors(selectors);
			var dataChanged = false;
			var sourceIdsToUpdate = {};
			var itemsUpdated = [];
			_.each(this._getPageContextEntries(selectors), function(pageContextEntry) {
				var thisEntryCleared = pageContextEntry.clearValues(options);
				if (thisEntryCleared) {
					itemsUpdated.push(pageContextEntry.getAPI());
				}
				dataChanged = (!dataChanged) ? thisEntryCleared : dataChanged;
				this._updateModifiedAssets(sourceIdsToUpdate, thisEntryCleared, pageContextEntry);
			}.bind(this));
			if (dataChanged && !options.silent) {
				//Notify others if the collection changed as a result of calling this
				selectors.sourceIdsToUpdate = sourceIdsToUpdate;
				this._filterContextUpdated(selectors, /*response*/ {
					cleared: true,
					updated: true,
					items: itemsUpdated,
					payloadData: payloadData
				});
			}
			return dataChanged;
		},

		/**
		 * Re-orders the page context items
		 * @param {object} sourceItems - the items to reOrder (could be a single item or an array).
		 * @param {object} insertBeforeItem - the item to insert the items before
		 */
		reOrderPageContextItems: function(sourceItems, insertBeforeItem) {
			var undoRedoTransactionId = UniqueID.get('reorder_filters_');
			sourceItems = _.isArray(sourceItems) ? sourceItems : [sourceItems];
			var insertBeforeModel = (insertBeforeItem) ? this._getEntry(insertBeforeItem.getKey()) : null;
			_.each(sourceItems, function(sourceItem) {
				var sourceModel = this._getEntry(sourceItem.getKey());
				this.reOrder(sourceModel, insertBeforeModel, undoRedoTransactionId);
			}.bind(this));
		},

		/**
		 * TODO: Consider making this method more generic and moving to base collection.
		 * Move the sourceModel to a position before the insertBeforeModel.
		 * If this is part of a compound re-order, an undoRedoTransactionId can be used.
		 * @param sourceModel - the model to move
		 * @param insertBeforeModel - the model to move this model before (or null to move to the end)
		 */
		reOrder: function(sourceModel, insertBeforeModel, undoRedoTransactionId) {
			//Find all of the models that correspond to the items (apis) that are going to move to before the insertBeforeItem.
			var nextSiblingModel = this._getNextModel(sourceModel);
			if (this._insertAt(sourceModel, insertBeforeModel)) {
				//When re-ordering, we're setting the item to before the new insertBeforeItem (null insert point signifies end of collection)
				//When undoing, we're moving the item to before the item it used to be before (null insert point signifies end of collection)

				var payload = {
					value: {
						op: 'reorder',
						sourceId: sourceModel.id,
						insertBeforeId: (insertBeforeModel) ? insertBeforeModel.id : null
					},
					prevValue: {
						op: 'reorder',
						sourceId: sourceModel.id,
						insertBeforeId: (nextSiblingModel) ? nextSiblingModel.id : null
					},
					//TODO: It should be possible to do this from the perspective of the sourceModel (pageContextEntry) rather than this.
					//		and remove the reference to modelKey to avoid the hack below.  This function can then be made part of the base collection.
					//		To be investigated.
					sender: this,
					senderContext: {
						applyFnKey: '_applyFn_reorder',
						applyFn: this._applyFn_reorder.bind(this),
						modelKey: 'pageContext' // Hack! - Allows us to look up the sending model when restoring from undo/redo spec
					},
					data: {
						undoRedoTransactionId: undoRedoTransactionId
					}
				};
				this.trigger('pagecontext:reorder', payload);
			}
		},

		/**
		 * The apply function that gets called by undo/redo
		 * @param {object} action - the action to apply
		 */
		_applyFn_reorder: function(action) {
			var itemToInsert = (action.sourceId) ? this.get(action.sourceId) : null;
			var insertBefore = (action.insertBeforeId) ? this.get(action.insertBeforeId) : null;

			if (itemToInsert) {
				this._insertAt(itemToInsert, insertBefore);
				this._filterContextUpdated({
					multi: true
				}, /*response*/ {
					updated: true,
					items: [itemToInsert]
				});
			}
		},

		/**
		 * Gets number of page context items
		 *
		 * @return {number} - the number of page context items
		 */
		getNumOfPageContextItems: function() {
			return this.models.length;
		},

		/**
		 * reset the matching items that match the selector to their persisted values. I.e the values that are not tagged with runtimeOnly
		 * @param selector - the items to select
		 * @returns nothing
		 */
		resetToPersistedValues: function(selector, options) {
			var items = this.getPageContextItems(selector);

			items = items.filter(function(item) {
				return item.resetToPersistedValues(options);
			});
			if (items.length) {
				this._filterContextUpdated(selector, /*response*/{ updated: true, items: items });
			}
		},

		/**
		 * delete all page context entries that match the passed in selectors.
		 * if the input selectors are null, reset the collection.
		 * throw the filterContextUpdated event with whatever selectors are passed in.
		 * @param selectors - any item selector.  If not specified, the pageContext will be emptied.
		 * @param deleteOptions - when set, this function does not transmit the state change externally.
		 * @returns A boolean indicating if any items were removed.
		 */
		deletePageContextItems: function(selectors, deleteOptions) {
			var result = this._doDeletePageContextItems(selectors, deleteOptions);
			this._doFilterContextUpdated(selectors, deleteOptions, result);
			return result.removedList && !!result.removedList.length;
		},

		_removeLayoutsHandler: function(eventPayload) {
			let ids = eventPayload.value.parameter;
			let deleteOptions = {
				payloadData: eventPayload.data,
				sender: eventPayload.sender
			};

			let result = [];
			_.each(ids, function(id) {
				let selectors = {
					scope: id
				};
				result.push(this.deletePageContextItems(selectors, deleteOptions));
			}.bind(this));

			return result;
		},

		/**
		 * Actions to take when there have been changes in the shaping model. Currently, if something was removed from the shaping model we will mark
		 * any page context models that no longer have a corresponding shaping model column with a flag to render them accordingly.
		 * @param payload - payload passed from 'shapingmodel:changed' event
		 * @returns A promise.
		 */
		_onShapingModelChanged: function(payload) {
			let promise = Promise.resolve();
			payload = payload || {};
			if (this.models && this.models.length) {
				let source = this._getDataSources().getDataSource(payload.sourceId);
				let response = {
					items: []
				};
				let columnId = this._getColumnIdForAction(payload);
				_.each(this.models, function(model) {
					if (model.api.getItemId() === columnId) {
						// update metadata and mark as unavailable if the column no longer exists
						let metaDataColumn = source.getMetadataColumn(model.api.getItemId());
						var metadata = FilterMetadataHelper.getFilterMetadataInfo(metaDataColumn);
						if (_.isEmpty(metadata)) {
							metadata =  model.getMetadataInfo();
							metadata.isColumnUnavailable = true;
						} else if (payload.action === 'SET') {
							model.setFilterName(metaDataColumn.getLabel());
						}
						model.updateMetadata(metadata);
						model.updateConditions();
						response.items.push(model.api);
						response.updated = true;
					}
				}.bind(this));
				let eventPayload = new PageContextResult({
					source: {
						multi: true
					},
					response: response,
					dataSources: this._getDataSources()
				}).getAPI();

				this.dashboardApi.triggerDashboardEvent('pagecontext:shapingModelChanged', eventPayload);
			}
			return promise;
		},

		_getColumnIdForAction: function(payload) {
			let item = null;
			switch (payload.action) {
				case 'REMOVE': {
					item = payload.oldValue.queryItem || payload.oldValue.filter || payload.oldValue.calculation || payload.oldValue;
					break;
				}
				case 'ADD': {
					item = payload.newValue.queryItem || payload.newValue.filter || payload.newValue.calculation || payload.newValue;
					break;
				}
				default:
					item = payload.notification.notifier;
			}
			return item.idForExpression;
		},

		deleteVisTypePageContext: function(selectors, deleteOptions) {
			deleteOptions = deleteOptions || {};
			deleteOptions.handlers = {
				filterContextUpdated: function(result, options) {
					this._doFilterContextUpdated(selectors, options, result);
				}.bind(this)
			};
			this._doDeletePageContextItems(selectors, deleteOptions);
		},

		_doFilterContextUpdated: function(selectors, deleteOptions, result) {
			if (result.removedList && result.removedList.length) {
				if (selectors) {
					selectors = _.clone(selectors);
					selectors.sourceIdsToUpdate = result.sourceIdsToUpdate;
				}
				deleteOptions = deleteOptions || {};
				if (!deleteOptions.silent) {
					this._filterContextUpdated(selectors,
						/*response*/
						{
							deleted: true,
							items: result.removedItems,
							noSelfRefresh: deleteOptions.noSelfRefresh,
							payloadData: deleteOptions.payloadData
						});
				}
			}
		},

		_doDeletePageContextItems: function(selectors, deleteOptions) {
			deleteOptions = deleteOptions || {};
			var transactionId = (deleteOptions.payloadData ? deleteOptions.payloadData.undoRedoTransactionId : deleteOptions.transactionId) || UniqueID.get('clear_delete_values_');
			selectors = this._mapSelectors(selectors);
			var removedList = this.models; //By default (if there are no selectors, remove all.)
			var sourceIdsToUpdate = deleteOptions.sourceIdsToUpdate || {};
			var removedItems = deleteOptions.removedItems || [];
			if (!selectors) {
				selectors = {};
				this.reset(null, {
					silent: true
				});
			} else {
				removedList = this.remove(this._getPageContextEntries(selectors), {
					silent: true
				});
			}
			if (removedList && removedList.length) {
				_.each(removedList, function(removedEntry) {
					this._updateModifiedAssets(sourceIdsToUpdate, /*modified*/ true, removedEntry);
					removedItems.push(removedEntry.getAPI());
				}.bind(this));

				var actions = this._getUndoRedoActions(deleteOptions, selectors.origin === 'filter', removedList, true);

				//Send an event to the undoRedo controller with the removedList, then notify others (CardWidget, FilterShelf)
				this._triggerItemsChanged(
					/*action*/
					actions.undo,
					/*undoAction*/
					actions.redo,
					/*payloadData*/
					{
						undoRedoTransactionId: transactionId,
						transactionToken: deleteOptions.payloadData && deleteOptions.payloadData.transactionToken
					},
					deleteOptions.sender
				);
			}
			return {
				removedList,
				removedItems,
				sourceIdsToUpdate
			};
		},

		/**
		 * returns all page context items that match the selectors.
		 * @param selectors - a subset of the itemContext and include things such as the
		 * scope ('tab1|global'), type ('filter'|'visualization), sourceId (tableId 'abcde'), itemId (hierarchy ID or itemId eg: [0].[Genre])
		 */
		getPageContextItems: function(selectors) {
			this._updatePageContextEntriesBinning();
			selectors = this._mapSelectors(selectors);
			var pageContext = [];
			this.each(function(pageContextEntry) {
				if (this._isMatch(pageContextEntry, selectors)) {
					pageContext.push(pageContextEntry.getAPI());
				}
			}.bind(this));
			return pageContext;
		},

		/**
		 * Collections track all events fired by any contained model.  This override tracks changes to (pageContextEntries) when values (tupleSets) change
		 * and notifies the undo/RedoController via the 'pageContextEntry:valuesChanged' event.  The applyFn is set to handle the undo/redo.
		 * @param event - the event sent from the child pageContextEntry.
		 */
		_onModelEvent: function(event) {
			//Set up undoRedo handlers for events we're interested in.
			var undoRedoHandler = (event.eventName === 'change:tupleSet' ||
				event.eventName === 'change:conditions' ||
				event.eventName === 'change:exclude' ||
				event.eventName === 'change:filterName') ? this._onUndoRedoValuesChanged.bind(this, event) : null;

			if (undoRedoHandler) {
				//Setup undo redo apply function for values changed.
				_.defaults(event, {
					model: event.sender,
					collection: this
				});
				event.senderContext = {
					applyFn: undoRedoHandler
				};
				//The pageContext notifies the undoRedo controller that one of its contained models have changed.
				this.trigger(event.eventName, event);
				return;
			}

			PageContext.inherited('_onModelEvent', this, arguments);
		},

		_updatePageContextEntriesBinning: function() {
			// Triggered when we select a data item that has binning turned on.
			let _self = this;
			this.each(function(pageContextEntry) {
				if (pageContextEntry.origin === 'visualization') {
					let sourceContent = _self.dashboardApi.getCanvas().getContent(pageContextEntry.getEventSourceId());
					pageContextEntry.binningAPIs = sourceContent ? _self._constructSelectedBinningDataItems(pageContextEntry.hierarchies, sourceContent): [];
				}
			});
		},

		_constructSelectedBinningDataItems: function(hierarchies, content) {
			let res = [];
			let hierarchyUniqueNames = _.map(hierarchies, (hierarchy) => hierarchy.hierarchyUniqueName);

			let slotApis = content.getFeature('Visualization').getSlots().getSlotList();
			_.each(slotApis, (slotApi) => {
				_.each(slotApi.getDataItemList(), (dataItemApi) => {
					if (dataItemApi.getBinning() && hierarchyUniqueNames.includes(dataItemApi.getColumnId()) ) {
						res.push({
							itemId: dataItemApi.getColumnId(),
							id: dataItemApi.getId(),
							binningAPI: dataItemApi.getBinning()
						});
					}
				});
			});

			return res;
		},

		/**
		 * This applyFn is called when one or more values for an item have changed via undo/redo (see _onModelEvent above)
		 * This includes change of a values array (member list), range conditions or filter names.
		 * It notifies subscribers to the pagecontext:filterContextUpdated event with the item whose values have changed.
		 * @param event - the original event (which contains the pageContextEntry that was affected)
		 * @param value - the current value for the tupleSet to be re-instated.
		 * @param sender - The source of the event (which will be UndoRedoController).
		 */
		_onUndoRedoValuesChanged: function(event, value, sender, name, data) {
			event = event || {};
			data = data || {};
			this.dashboardApi.triggerDashboardEvent('brushing:undoRedo', data);
			var model = this.get(event.model.id);

			if (event.name === 'exclude') {
				var senderApi = event.sender.getAPI();
				senderApi.setExclude(value, {
					sender: sender
				});
			} else if (event.name === 'tupleSet') {
				// Make sure to persist the new tupleSet
				model.setTupleSetCb(value, {
					sender: sender
				});
			} else {
				model[event.name] = value;
			}

			//Set both the item and the items member in the response to indicate its a single result but to ease client processing.
			this._filterContextUpdated( /*source*/ {
				sourceId: sender
			},
				/*response*/
			{
				updated: true,
				ignorePageContextChanged: data.ignorePageContextChanged,
				items: [event.sender.getAPI()],
				payloadData: data
			});
		},

		/**
		 * This applyFn is called when one or more models (pageContextItems) are being added or removed from the pageContext collection.
		 * It adds/removes the individual entry (or entries) from the collection and notifies subscribers to the pagecontext:filterContextUpdated event
		 * (such as the cardWidget and filterShelf with the sourceIds (tables) that were affected).
		 * @param action - the action to perform on undo or redo (which includes an operator and parameter)
		 * @param sender - the name of the event source (which will be UndoRedoController).
		 */
		_onUndoRedoItemsChanged: function(action, sender, name, data) {
			data = data || {};
			var sourceIdsToUpdate = {};
			var contextEntrySpecs = action.parameter;
			var contextEntryItems = [];
			var brushingOptions = null;

			this.dashboardApi.triggerDashboardEvent('brushing:undoRedo', data);

			_.each(contextEntrySpecs, function(contextEntrySpec) {
				var contextEntry = new PageContextEntry(contextEntrySpec);
				this._setGlobalContextCb(contextEntry);
				contextEntryItems.push(contextEntry.getAPI());
				//Keep a reference to the sourceId's that were modified so that the CardWidget
				//can know whether a change affects it.
				sourceIdsToUpdate[contextEntry.getSourceId()] = true;
				if (sender === 'UndoRedoController') {
					if (contextEntry.origin === 'visualization') {
						//Should only ever get brushing changes for a single scope in 1 event.
						brushingOptions = {
							scope: contextEntry.scope
						};
					}

					if (action.op === 'remove' || action.op === 'removeAt') {
						this.remove(contextEntry, {
							sender: sender
						});
					} else if (action.beforeId && action.op === 'addAt') {
						this._insertAt(contextEntry, this._getEntry(action.beforeId));
					} else {
						this.add(contextEntry, {
							sender: sender
						});
					}
				}
			}.bind(this));

			//Signal to others that this is a 'multi'-update (multiple assets need to be updated or general re-render needs to occur)
			var response = {
				items: contextEntryItems,
				ignorePageContextChanged: data.ignorePageContextChanged
			};
			response.payloadData = data;

			if (action.op === 'remove') {
				response.deleted = true;
			} else {
				response.updated = true;
			}

			//NOTE: Currently, a single undoRedo of items should only have a set of filter changes OR a set of brushing changes (never both).
			if (brushingOptions) {
				this._brushingContextUpdated(brushingOptions, response);
			} else {
				this._filterContextUpdated({
					multi: true,
					sourceIdsToUpdate: sourceIdsToUpdate
				}, response);
			}
		},

		/**
		 * This function is required for undoRedo to use the pageContext, however;
		 * The pageContext does not use a child model so we simply return null here.
		 */
		findChildModel: function() {
			return null;
		},

		/**
		 * Combine the global and tab-specific context into a 'net context'.
		 * This type of pageContext is useful to cards (where the blended result will be its filter state)
		 * @param selectors - the portion of the page context to consider.
		 *			Usually should include a scope: 'tabId' (to be combined with global)
		 * @param filterItemContext - the context of filterItem which needs to be exclulded from net pageContext
		 * @returns the portion of the pageContext specification that defines the actual context.
		 */
		getNetPageContext: function(selectors, filterItemContext) {
			return this._getNetPageContext(selectors, filterItemContext, /*bSpecType*/ true);
		},
		/**
		 * Combine the global and tab-specific context into a 'net context'.
		 * This type of pageContext is useful to cards (where the blended result will be its filter state)
		 * @param selectors - the portion of the page context to consider.
		 *			Usually should include a scope: 'tabId' (to be combined with global)
		 * @returns the portion of the pageContext items that defines the actual context.
		 */
		getNetPageContextItems: function(selectors) {
			return this._getNetPageContext(selectors, null, /*bSpecType*/ false);
		},

		/**
		 * Combine the global and tab-specific context into a 'net context'.
		 * This type of pageContext is useful to cards (where the blended result will be its filter state)
		 * @param selectors - the portion of the page context to consider.
		 *			Usually should include a scope: 'tabId' (to be combined with global)
		 * @param filterItemContext - the context of filterItem which needs to be exclulded from net pageContext
		 * @param bSpecType - if true returned type is spec of pacge context ite, or item itself
		 * @returns the portion of the pageContext specification that defines the actual context.
		 */
		_getNetPageContext: function(selectors, filterItemContext, bSpecType) {
			selectors = this._mapSelectors(selectors);
			var netPageContext = [];
			var netContextMap = {};

			var scope = (selectors && selectors.scope) ? selectors.scope : 'global';
			var isForFilter, filterItemSelectors;
			if (filterItemContext) {
				isForFilter = true;
				filterItemSelectors = this._mapSelectors(filterItemContext);
			}

			var validSourceIds = this._getValidSourceIds(selectors, filterItemContext);

			this._updatePageContextEntriesBinning();

			this.each(function(pageContextEntry) {
				if (pageContextEntry.hasData() || pageContextEntry.getIsModeledFilter()) {
					//Only process entries that are on the current tab or are global.
					if ((!validSourceIds || (validSourceIds && validSourceIds.indexOf(pageContextEntry.sourceId) !== -1)) &&
						(pageContextEntry.scope === 'global' || pageContextEntry.scope === scope) &&
						(this._isMatch(pageContextEntry, selectors, /*excluding*/ ['scope']))) {
						if (!isForFilter) {
							this._updateNetContextMap(pageContextEntry, netContextMap, scope);
						} else if (!this._isMatch(pageContextEntry, filterItemSelectors)) {
							//If it is for filter, exclude itself and add other items
							this._updateNetContextMap(pageContextEntry, netContextMap, scope);
						}
					}
				}
			}.bind(this));

			_.keys(netContextMap).forEach(key => {
				const contextItem = netContextMap[key];
				const pageContextItem = contextItem.getPageContextSpecItem();
				// Do not include pageContext entries that include no values.
				if (pageContextItem.isModeledFilter || pageContextItem.exclude || pageContextItem.conditions || (pageContextItem.tupleSet && pageContextItem.tupleSet.length) || contextItem.getIsNamedSet()) {
					if (bSpecType) {
						netPageContext.push(pageContextItem);
					} else {
						netPageContext.push(contextItem.getAPI());
					}
				}

			});

			return netPageContext;
		},

		_getValidSourceIds: function(selectors, filterItemContext) {
			var sourceId = selectors && selectors.sourceId ? selectors.sourceId : filterItemContext ? filterItemContext.sourceId : null;
			if (!sourceId) {
				return null;
			}
			const dataSources = this._getDataSources();
			const dataSourceList = dataSources.getDataSourceList();
			const source = dataSources.getDataSource(sourceId);
			return source ? dataSourceList.filter((dataSource) => dataSource.getAssetId() === source.getAssetId()).map((dataSource) => dataSource.getId()) : null;
		},

		/**
		 * Apply a set of precedence rules to determine who contributes to the netContext.
		 * The precedence rules are as follows:
		 * 1) If only a global filter has been defined on an item, the global context is the only contributor
		 * 2) If only a tab/scoped filter has been defined on an item, the tab filter is the only contributor
		 * 3) If both a global and a tab filter have been defined on the same item,
		 *		the net context is this intersection of the two (accessed through the tab one and its global pointer).
		 * @param pageContextEntry - the page context item being processed.
		 * @param netContextMap - a map containing the selected entry' (which should always be defined and could be either global or tab context)
		 * @param scope - the selected tab (scope) used to determine the net context (which is a combination of this tab and global)
		 */
		_updateNetContextMap: function(pageContextEntry, netContextMap, scope) {
			//Ask for an unscoped key so that the global and tab-specific keys for the same item are identical.
			const netContextKey = pageContextEntry.getUnscopedKey();
			//For non-range filter, if filter's tupleSet is empty for global or tab/scoped filters in non-exclude mode, the netContextMap should not update the value with empty tupleSet, caused NP issue
			if (!pageContextEntry.isModeledFilter && !pageContextEntry.isRange() && !pageContextEntry.getValueCount() && !pageContextEntry.exclude && !pageContextEntry.getIsNamedSet()) {
				return;
			}
			if (!netContextMap[netContextKey]) {
				netContextMap[netContextKey] = pageContextEntry;
			} else if (pageContextEntry.scope === scope) {
				netContextMap[netContextKey] = pageContextEntry;
			}
		},

		/**
		 * provide the net page context as a versioned spec.
		 * @param selectors - the portion of the page context to extract into a net context.
		 * @returns a JSON object of a form defined in the pageContext specification.
		 */
		getNetPageContextSpec: function(selectors) {
			var netPageContext = this.getNetPageContext(selectors);
			return {
				version: '1.00',
				pageContext: netPageContext
			};
		},

		/**
		 * returns a single page context item and throws an error if more than one matches.
		 * @param selectors - a subset of the itemContext and include things such as the
		 * 
		 * @returns the matching pageContextItem.
		 */
		getPageContextItem: function(selectors) {
			var pageContextItems = this.getPageContextItems(selectors);
			if (pageContextItems.length > 1) {
				this.dashboardApi.getGlassCoreSvc('.Logger').error('selector is not unique!', this);
			}
			return pageContextItems[0];
		},

		/**
		 * updates eventGroupId property on matching pageContext Entries
		 * @param eventSourceId - selector
		 * @param scope - the scope of event source belogs to
		 * @param newEventGroupId - new event group id
		 * @param options
		 * @returns the matching pageContextItem.
		 */
		updateEventGroupId: function(eventSourceId, scope, newEventGroupId, options) {
			let updatedEntries = [];
			this.each(function(entry) {
				if (entry.eventSourceId === eventSourceId) {
					entry.updateEventGroupId(newEventGroupId, options);
					updatedEntries.push(entry);
				}

				if (entry.origin === 'visualization' && entry.scope === scope) {
					this._filterContextUpdated({
						eventSourceId: entry.eventSourceId
					}, /*response*/ {
						updated: true,
						items: [entry]
					}, options);
				}
			}.bind(this));
			this._replaceEntriesWithChangedKeyTosyncModelMap(updatedEntries, options);
		},

		/**
		 * since id of entry changed, we will replace whole entry to keep modelmap sync
		 */
		_replaceEntriesWithChangedKeyTosyncModelMap(updatedEntries, options) {
			_.each(updatedEntries, updatedEntry => {
				this.remove(updatedEntry, options);
				this.add(updatedEntry, options);
			});
		},

		/**
		 * For updating, the selector should be sufficient to specify a unique entry
		 * @param itemContext - the item selector
		 */
		_checkItemContext: function(itemContext) {
			if (itemContext && !itemContext.item) {
				if ((!itemContext.sourceId || !itemContext.hierarchyUniqueNames)) {
					this._onError('PAGECONTEXT: itemContext is not unique!');
				}
			}
		},

		/**
		 * @param item - a page context entry that is being compared
		 * @param subset - match selectors
		 * @param subset._strictMatch - if set to true, it bypasses the ignoreSet
		 * @param ignoreList - a list that is added to ignoreSet
		 */
		_isMatch: function(item, subset, ignoreList) {
			//we don't want subset._strictMatch to be part of comparison
			var strictMatch = false;
			if (subset && !!subset._strictMatch) {
				strictMatch = true;
			}
			let matchSelectors = _.omit(subset, '_strictMatch');

			//These items will never be considered when determining if entries are a match.
			var ignoreSet = {
				metadataSourceId: 1, //Ignore (but save) the cardId that is providing the metadata for this context item (could change)
				sourceId: 1, //Ignore the sourceId (an event source specified by the filter control)
				filterName: 1, //Ignore (but save) the filterName (an alias) that a user can set in the filter control.
				multi: 1, //Ignore the multi selector flag (it has nothing to do with the items being selected.),
				dataType: 1, //ignore, dataType isn't a matching criteria
				hierarchiesContain: 1, //Ignore hierarchiesContain.  It is processed in a separate matcher.
				isHierarchy: 1, //Ignore isHierarchy,
				eventSourceId: 1, //Ignore eventSourceId
				usage: 1, //Ignore usage
				isNamedSet: 1
			};
			//The caller can add additional entries to the ingoreList.
			_.each(ignoreList, function(ignoreString) {
				ignoreSet[ignoreString] = 1;
			});
			//Add eventGroupId to ignore set for Filters (non visualization)
			if (item.origin !== 'visualization') {
				ignoreSet.eventGroupId = 1;
			}

			for (var attr in matchSelectors) {
				if (!matchSelectors.hasOwnProperty(attr)) {
					continue;
				}
				if (!strictMatch && ignoreSet[attr]) {
					continue;
				}

				if (attr === 'hierarchies' && item[attr]) {
					// the order of values in hierarchies should be ignored, so that value1 and value2 is matched
					// value1: [{hierarchyUniqueName: "Order_method"} {hierarchyUniqueName: "Product_line"}]
					// value2: [{hierarchyUniqueName: "Product_line"} {hierarchyUniqueName: "Order_method"}]
					var tempSelectors = _.clone(matchSelectors[attr]); //Make a copy to sort and keep subset[attr] unchanged.
					var sortedSelectors = tempSelectors.sort(this._compareHierarchies);
					var tempItem = _.clone(item[attr]); //Make a copy to sort and keep item[attr] unchanged.
					var sortedItem = tempItem.sort(this._compareHierarchies);

					if (!_.isEqual(sortedSelectors, sortedItem)) {
						return false;
					}
				} else if (!_.isEqual(matchSelectors[attr], item[attr])) {
					return false;
				}

			}
			return true;
		},

		_compareHierarchies: function(a, b) {
			if (a.hierarchyUniqueName < b.hierarchyUniqueName) {
				return -1;
			}
			if (a.hierarchyUniqueName > b.hierarchyUniqueName) {
				return 1;
			}
			return 0;
		},

		//Performs regular matching except that, if hierarchiesContain is specified, it will search
		//the hierarchies array in each entry and return true if the hierarchyUniqueName of ANY hierarchy matches the hierarchiesContain value.
		_isMatchWithHierarchiesContainSelector: function(pageContextEntry, selectors) {
			if (selectors.hierarchiesContain) {
				if (this._isMatch(pageContextEntry, selectors, /*ignoreList*/ ['hierarchies', 'hierarchyUniqueNames'])) {
					var hierarchiesUsedInThisEntry = _.map(pageContextEntry.hierarchies, function(hierarchy) {
						return hierarchy.hierarchyUniqueName;
					});
					var matches = _.intersection(hierarchiesUsedInThisEntry,
						(Array.isArray(selectors.hierarchiesContain) ? selectors.hierarchiesContain : [selectors.hierarchiesContain]));
					return (matches && matches.length) ? true : false;
				}
			}
			return false;
		},

		_getPageContextEntries: function(selectors) {
			if (selectors.item) {
				//If the special selector 'item' is used, match the item against the api of the entry.
				var itemFound = this.find(function(pageContextEntry) {
					return pageContextEntry.getAPI() === selectors.item;
				});
				return (itemFound) ? [itemFound] : [];
			}

			var pageContextEntries = [];
			this.each(function(pageContextEntry) {
				if (selectors.hierarchiesContain) {
					if (this._isMatchWithHierarchiesContainSelector(pageContextEntry, selectors)) {
						pageContextEntries.push(pageContextEntry);
					}
				} else if (this._isMatch(pageContextEntry, selectors)) {
					pageContextEntries.push(pageContextEntry);
				}
			}.bind(this));
			return pageContextEntries;
		},


		//Given the itemContext, find a matching pageContextEntry...if it doesn't exist add it.
		_findOrCreateFilterContextEntry: function(itemContext, update, options, response) {
			var existingEntries = this._getPageContextEntries(itemContext);
			var existingEntry = (existingEntries && existingEntries.length > 0) ? existingEntries[0] : null;
			if (!existingEntry) {
				response.added = true;
				response.beforeItem = options.beforeItem;
				existingEntry = this._addFilterContextEntry(itemContext, update.condition, options);
			}
			return existingEntry;
		},

		_addFilterContextEntry: function(selectors, rangeCondition, options) {
			//There must be a hierarchyUniqueName in the context entry.
			//If the selector specifies an itemId, it is considered an array of 1 hierarchyUniqueName

			var contextEntrySpec = {
				origin: 'filter',
				sourceId: selectors.sourceId,
				table: '',
				alias: '0',
				scope: 'global',
				metadataSourceId: selectors.metadataSourceId,
				dataType: selectors.dataType,
				isModeledFilter: selectors.isModeledFilter
			};
			_.each(_.keys(selectors), function(key) {
				contextEntrySpec[key] = selectors[key];
			});

			if (rangeCondition) {
				contextEntrySpec.condition = rangeCondition;
			}
			contextEntrySpec.selectionInfo = options.selectionInfo ? options.selectionInfo : null;

			return this._addPageContextEntry(contextEntrySpec, options);
		},

		_addPageContextEntry: function(contextEntrySpec, options = {}) {
			//Default silent to true when adding a pageContextEntry.
			const pageContextEntryOptions = { ...options };
			pageContextEntryOptions.silent = (pageContextEntryOptions.silent === false) ? false : true;

			var isFilterOrigin = (contextEntrySpec.origin === 'filter') ? true : false;
			var contextEntry = new PageContextEntry(contextEntrySpec);
			this._setGlobalContextCb(contextEntry);

			if (this.get(contextEntry)) {
				return this.get(contextEntry);
			}

			if (isFilterOrigin && pageContextEntryOptions.beforeItem) {
				this._insertAt(contextEntry, this._getEntry(pageContextEntryOptions.beforeItem.getKey()), pageContextEntryOptions);
			} else {
				this.add([contextEntry], pageContextEntryOptions);
			}
			var actions = this._getUndoRedoActions(pageContextEntryOptions, isFilterOrigin, contextEntrySpec);
			this._triggerItemsChanged( /*action*/ actions.undo, /*undoAction*/ actions.redo, pageContextEntryOptions.payloadData);
			return contextEntry;
		},

		_getUndoRedoActions: function(options, isFilterOrigin, contextEntrySpec, isDelete) {

			options = options || {};
			contextEntrySpec = contextEntrySpec || [];

			var contextEntrySpecs = _.isArray(contextEntrySpec) ? contextEntrySpec : [contextEntrySpec];

			var actions = {
				undo: {
					parameter: contextEntrySpecs
				},
				redo: {
					parameter: contextEntrySpecs
				}
			};

			//If beforeItem is null, add or remove at the end of the collection.
			if (isFilterOrigin && options.beforeItem) {
				var beforeModel = this._getEntry(options.beforeItem.getKey());
				actions.undo.op = isDelete ? 'removeAt' : 'addAt';
				actions.redo.op = isDelete ? 'addAt' : 'removeAt';
				actions.undo.beforeId = actions.redo.beforeId = (beforeModel) ? beforeModel.id : null;
			} else {
				actions.undo.op = isDelete ? 'remove' : 'add';
				actions.redo.op = isDelete ? 'add' : 'remove';
			}

			return actions;
		},

		/**
		 * Creates a copy of the page context items matching the selectors and
		 * assign them a new scope (re-scope).
		 * @param {Object} payload
		 * @param {Object} payload.selectors - Matching criteria for page context items.
		 * @param {string} payload.newScope - scope to be assigned to the copy of page context
		 * items.
		 */
		copyPageContextItemsAndRescope: function(payload) {
			var newScope = payload.cloneId;
			var mappedselector = this._mapSelectors({
				scope: payload.layoutId
			});
			var pageContextEntries = this._getPageContextEntries(mappedselector);
			var rescopedEntries = [];

			if (pageContextEntries.length === 0) {
				return rescopedEntries;
			}

			pageContextEntries.forEach(function(pageContextEntry) {
				var spec = pageContextEntry.toJSON();

				if (spec.eventGroupId) {
					//This is a brushing entry...need to manage the old and new event groups.
					if (payload.eventGroupId) {
						//A single target eventGroup was specified (eg: all entries matching the selectors will be placed in this event group).
						spec.eventGroupId = payload.eventGroupId;
					} else {
						//The event group will be the same as it was initially but re-scoped to the page specified by cloneId.
						spec.eventGroupId = spec.eventGroupId.replace(spec.scope, newScope);
					}
				}

				spec.scope = newScope;

				var newTabEntry = new PageContextEntry(spec);
				this._setGlobalContextCb(newTabEntry);
				rescopedEntries.push(newTabEntry);
			}.bind(this));

			this.add(rescopedEntries, {
				silent: true
			});

			//Send an event to the undoRedo controller with the rescopedEntries
			this._triggerItemsChanged(
				/*action*/
				{
					op: 'add',
					parameter: rescopedEntries
				},
				/*undoAction*/
				{
					op: 'remove',
					parameter: rescopedEntries
				},
				/*payloadData*/
				{
					undoRedoTransactionId: payload.undoRedoTransactionId,
					transactionToken: payload.transactionToken
				}
			);
			return rescopedEntries;
		},

		/**
		 * trigger an event to the undo redo controller to be handled by the pageContext.
		 * @param action - the action that was performed (the current value)
		 * @param undoAction - the action that undoing would perform
		 * @param payloadData - optional payloadData to attach to the event.
		 * @param sender - optional event sender (e.g. UndoRedoController), defaults to 'this'
		 */
		_triggerItemsChanged: function(action, undoAction, payloadData, sender) {
			var payload = {
				value: action,
				prevValue: undoAction,
				//TODO: It should be possible to use the pageContextEntry as the sender instead of this (pageContext)
				//		and remove the reference to modelKey to avoid the hack below. To be investigated.
				sender: sender || this,
				senderContext: {
					applyFnKey: '_onUndoRedoItemsChanged',
					applyFn: this._onUndoRedoItemsChanged.bind(this),
					modelKey: 'pageContext' // Hack! - Allows us to look up the sending model when restoring from undo/redo spec
				},
				data: payloadData,
				name: 'itemsChanged'
			};
			this.trigger('pagecontext:itemsChanged', payload);
			return payload;
		},

		/**
		 * Notify objects on the page that an update of type 'filter' has occurred for a particular source.
		 * @param source - 	a source as defined above.  A target of this event would decide if it were interested
		 *					in this update and, if so, call getFilterContext on that source to get it.
		 * @param response -  a response which describes how the pageContext was modified which includes an item (or list of items)
		 *					and whether those items were deleted, updated or added.
		 */
		_filterContextUpdated: function(source, response) {

			var payload = new PageContextResult({
				source: source,
				response: response,
				dataSources: this._getDataSources()
			}).getAPI();
			payload.beforeItem = response.beforeItem;
			payload.data = response.payloadData;
			this.dashboardApi.triggerDashboardEvent('pagecontext:filterContextUpdated', payload);
		},

		/**
		 * Notify objects on the page that an update of type 'visualization' has occurred for a particular source.
		 * @param brushingOptions - source options for this brushing update (includes the sourceId of the update if available).
		 *							A target of this event would decide if it were interested
		 *							in this update and, if so, call getFilterContext on that source to get it.
		 * @param response -  		a response which describes how the pageContext was modified which includes an item (or list of items)
		 *							and whether those items were deleted, updated or added.
		 */
		_brushingContextUpdated: function(brushingOptions, response) {

			var payload = new PageContextResult({
				brushingOptions: brushingOptions,
				response: response,
				dataSources: this._getDataSources()
			}).getAPI();
			payload.data = response.payloadData;
			this.dashboardApi.triggerDashboardEvent('pagecontext:brushingContextUpdated', payload);
		},

		/**
		 * the global context callback function allows a tab filter to access its associated global context entry
		 * so that its taken into account when querying for selected values.
		 * @param pageContextEntry - the page context entry to set the global context for.
		 */
		_setGlobalContextCb: function(pageContextEntry) {
			var getGlobalContextCb = function(globalKey) {
				return this.get(globalKey);
			};

			if (pageContextEntry.getScope() !== 'global') {
				var globalKey = pageContextEntry.getGlobalKey();
				pageContextEntry.setGlobalContextCb(getGlobalContextCb.bind(this, globalKey));
			}
		},

		/**
		 * throw the specified error to the caller.
		 * @param msg - the message to passe
		 */
		_onError: function(msg) {
			throw new Error(msg);
		},

		/**
		 * Calculates the insert before item
		 *
		 * @param {object} itemOrEntryKey	- the key to lookup.....note: we can probably use get().
		 */
		_getEntry: function(itemOrEntryKey) {
			if (itemOrEntryKey) {
				return this.modelMap[itemOrEntryKey];
			}
			return null;
		},

		/**
		 * given any model in this collection, find its next sibling model (or null)
		 * @param model - the model to find the next sibling for
		 * @returns the sibling model or null.
		 */
		_getNextModel: function(modelToFind) {
			var indexOfModel = this._getModelIndex(modelToFind);
			return (indexOfModel < this.models.length - 1) ? this.models[indexOfModel + 1] : null;
		},

		_getModelIndex: function(modelToFind) {
			var indexOfModel = -1;
			if (this.models) {
				for (var idx = 0; idx < this.models.length; idx++) {
					if (this.models[idx] === modelToFind) {
						indexOfModel = idx;
						break;
					}
				}
			}
			return indexOfModel;
		},

		/**
		 * TODO: Consider making this method more generic and moving to base class.
		 * Insert a new model before the insertBeforeModel.
		 * If the insertBeforeModel is null, put the modelsToBeInserted at the end of the array.
		 * If any of the modelsToBeInserted already exist in the collection, remove them from their old location prior to inserting them at the new position)
		 *
		 * @param {object} modelToInsert - the model to insert before the insertBeforeModel (if insertBeforeModel is null, insert at the end.)
		 * @param {object} insertBeforeModel - The options containing the context use to insert/re-order the models.
		 *
		 * @return {array} Array of inserted index locations
		 */
		_insertAt: function(modelToInsert, insertBeforeModel, options) {
			if (!modelToInsert) {
				return false; //Nothing to do.
			}

			var indexOfModelToMove = this._getModelIndex(modelToInsert);
			if (indexOfModelToMove === -1) {
				//If this model isn't already in the collection, add it first.
				options = options || {
					silent: true
				};
				this.add([modelToInsert], options);
				indexOfModelToMove = this._getModelIndex(modelToInsert);
			}

			//Remove the model from its old location and insert it into the new one.
			this.models.splice(indexOfModelToMove, 1);
			if (insertBeforeModel) {
				this.models.splice(this._getModelIndex(insertBeforeModel), 0, modelToInsert);
			} else {
				this.models.push(modelToInsert);
			}
			return true;
		},

		_getDataSources: function(){
			if(!this.dataSources){
				this.dataSources = this.dashboardApi.getFeature('DataSources');
			}
			return this.dataSources;
		}
	});
	return PageContext;
});
